export class NotificationService {
    // Pre-load the notification sound
    static notificationSound = null;
    static audioContext = null;
    static audioBuffer = null;
    static audioInitialized = false;
    static userHasInteracted = false;
    static interactionPromise = null;
    static interactionResolve = null;

    static async initAudio() {
        // Don't re-initialize if already done
        if (this.audioInitialized) return;

        try {
            // Create a fallback Audio element - this is safer to create without user interaction
            if (!this.notificationSound && typeof Audio !== 'undefined') {
                // Use a full URL to ensure the sound file is found correctly
                const soundUrl = new URL('/notification.wav', window.location.origin).href;
                this.notificationSound = new Audio(soundUrl);
                // Preload the sound
                this.notificationSound.load();
            }

            // We'll defer AudioContext creation until user interaction
            // This will be handled in the playNotificationSound method

            this.audioInitialized = true;
        } catch (error) {
        }
    }

    static preloadSound() {
        // Initialize audio system
        this.initAudio();

        // Set up interaction promise if not already done
        if (!this.interactionPromise) {
            this.interactionPromise = new Promise(resolve => {
                this.interactionResolve = resolve;
            });
        }

        // Add event listeners to multiple user interactions to ensure audio is unlocked
        const unlockAudio = () => {

            // Mark that user has interacted with the page
            this.userHasInteracted = true;

            // Resolve the interaction promise if it exists
            if (this.interactionResolve) {
                this.interactionResolve();
            }

            // Create AudioContext on user interaction if it doesn't exist
            if (!this.audioContext && window.AudioContext) {
                try {
                    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

                    // Now that we have a user interaction, we can load the audio buffer
                    this.loadAudioBuffer();
                } catch (e) {
                }
            }

            // Resume AudioContext if it was suspended
            if (this.audioContext && this.audioContext.state === 'suspended') {
                this.audioContext.resume().then(() => {
                }).catch(e => console.warn('Failed to resume AudioContext:', e));
            }

            // Also try to play a silent sound to unlock audio on iOS
            try {
                const soundUrl = new URL('/notification.wav', window.location.origin).href;
                const tempAudio = new Audio(soundUrl);
                tempAudio.volume = 0.01; // Very low volume
                tempAudio.play().catch(() => {});
            } catch (e) {
            }
        };

        // Listen for various user interactions to unlock audio
        ['click', 'touchstart', 'touchend', 'mousedown', 'keydown'].forEach(event => {
            document.addEventListener(event, unlockAudio, { once: true });
        });

        // Also try to unlock on page visibility change (when user returns to the tab)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && this.userHasInteracted) {
                unlockAudio();
            }
        });
    }

    // Load audio buffer for Web Audio API
    static async loadAudioBuffer() {
        if (!this.audioContext || this.audioBuffer) return;

        try {
            const response = await fetch('/notification.wav');
            const arrayBuffer = await response.arrayBuffer();
            this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        } catch (error) {
        }
    }

    // Play notification sound with fallbacks
    static async playNotificationSound(soundUrl = '/notification.wav') {

        // Ensure we have a full URL
        const fullSoundUrl = new URL(soundUrl, window.location.origin).href;

        // Make sure audio is initialized
        if (!this.audioInitialized) {
            await this.initAudio();
        }

        // If user hasn't interacted yet, we need to queue the sound for later
        if (!this.userHasInteracted) {

            // Store the sound request for later
            const pendingSoundPlay = async () => {
                try {
                    // Wait a moment after interaction to ensure audio context is fully ready
                    await new Promise(resolve => setTimeout(resolve, 300));
                    // Try to play the sound again after user interaction
                    await this.playNotificationSound(soundUrl);
                } catch (error) {
                }
            };

            // If we already have an interaction promise, use it
            if (this.interactionPromise) {
                this.interactionPromise.then(pendingSoundPlay);
                return false; // Indicate that sound didn't play immediately
            }

            return false; // Can't play now and no interaction promise available
        }

        // Try multiple approaches to play the sound
        try {

            // First, try to use the Web Audio API if available and initialized
            if (this.audioContext) {
                // Make sure the context is running
                if (this.audioContext.state === 'suspended') {
                    try {
                        await this.audioContext.resume();
                    } catch (e) {
                    }
                }

                // If we have a buffer, use it
                if (this.audioBuffer) {
                    try {
                        const source = this.audioContext.createBufferSource();
                        source.buffer = this.audioBuffer;
                        source.connect(this.audioContext.destination);
                        source.start(0);
                        return true;
                    } catch (e) {
                        // Continue to fallbacks
                    }
                }
                // Otherwise, try to create a new buffer source from the URL
                else {
                    try {
                        // Create a new buffer on demand
                        const response = await fetch(fullSoundUrl);
                        if (!response.ok) {
                            throw new Error(`Failed to fetch sound file: ${response.status} ${response.statusText}`);
                        }
                        const arrayBuffer = await response.arrayBuffer();
                        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

                        // Save the buffer for future use
                        this.audioBuffer = audioBuffer;

                        const source = this.audioContext.createBufferSource();
                        source.buffer = audioBuffer;
                        source.connect(this.audioContext.destination);
                        source.start(0);
                        return true;
                    } catch (e) {
                        // Continue to fallbacks
                    }
                }
            }

            // Fallback to HTML5 Audio element
            if (this.notificationSound) {
                try {
                    this.notificationSound.currentTime = 0;
                    this.notificationSound.volume = 1.0;
                    await this.notificationSound.play();
                    return true;
                } catch (e) {
                    // Continue to fallbacks
                }
            }

            // Last resort: create a new Audio element
            try {
                const audio = new Audio(fullSoundUrl);
                audio.volume = 1.0;
                await audio.play();
                return true;
            } catch (e) {
                throw e; // Re-throw to be caught by the outer catch
            }

        } catch (error) {
            return false;
        }
    }

    static async requestPermission() {
        if (!('Notification' in window)) {
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        const permission = await Notification.requestPermission();
        return permission === 'granted';
    }

    static async showNotification(title, options = {}) {
        if (!await this.requestPermission()) {
            return;
        }

        // Make sure audio is initialized
        await this.initAudio();

        // Default options
        const defaultOptions = {
            icon: '/pwa/pwa-192x192.png',
            badge: '/pwa/pwa-192x192.png',
            vibrate: [200, 100, 200],
            requireInteraction: true,
            sound: '/notification.wav',
            data: {
                url: options.url || '/notifications'
            }
        };

        const notification = new Notification(title, {
            ...defaultOptions,
            ...options
        });

        // Play notification sound using our dedicated method
        if (defaultOptions.sound) {
            // We don't await this to avoid blocking the notification
            this.playNotificationSound(defaultOptions.sound)
                .then(success => {
                    if (success) {
                    } else {
                        // If sound didn't play because user hasn't interacted, we'll handle it when they do interact
                    }
                })
                .catch(() => {
                });
        }

        // Handle click
        notification.onclick = (event) => {
            event.preventDefault();
            window.focus();
            if (notification.data.url) {
                window.location.href = notification.data.url;
            }
            notification.close();
        };

        return notification;
    }
}
